//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckmarkFilled, ErrorFilled } from '@carbon/icons-react';
import { Button } from '../ui/button';
import TenantDataService from '../../utils/TenantDataService';
import type {
  ProductPropagationStatusEntry,
  AiPropagationStatusResponse,
} from '../Sidebar/SidebarTypes';

type ProductStatus = 'waiting' | 'running' | 'success' | 'failed';

interface PropagationProgressProps {
  teamspaceId: string;
  tenantDataService: TenantDataService;
  onDone: () => void;
}

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 60;

const PRODUCT_LABELS: Record<string, string> = {
  TEST: 'Test',
  PLAN: 'Plan',
  DEPLOY: 'Deploy',
};

const PropagationProgress: React.FC<PropagationProgressProps> = ({
  teamspaceId,
  tenantDataService,
  onDone,
}) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<string[]>([]);
  const [productStatuses, setProductStatuses] = useState<
    Record<string, ProductStatus>
  >({});
  const [productErrors, setProductErrors] = useState<Record<string, string>>(
    {},
  );
  const [isComplete, setIsComplete] = useState(false);
  const [hasFailure, setHasFailure] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const cancelledRef = useRef(false);

  const mapStatus = (entry: ProductPropagationStatusEntry): ProductStatus => {
    switch (entry.status) {
      case 'SUCCESS':
        return 'success';
      case 'FAILED':
      case 'RETRY_SCHEDULED':
        return 'failed';
      case 'RUNNING':
        return 'running';
      default:
        return 'waiting';
    }
  };

  const pollStatus = useCallback(
    async (expectedProducts: string[]) => {
      let attempts = 0;

      while (attempts < MAX_POLL_ATTEMPTS && !cancelledRef.current) {
        attempts++;
        try {
          const status: AiPropagationStatusResponse =
            await tenantDataService.getAiPropagationStatus(teamspaceId);

          if (cancelledRef.current) return;

          const nextStatuses: Record<string, ProductStatus> = {};
          const nextErrors: Record<string, string> = {};

          (status.products || []).forEach((entry) => {
            nextStatuses[entry.product] = mapStatus(entry);
            if (entry.lastError) {
              nextErrors[entry.product] = entry.lastError.message;
            }
          });

          setProductStatuses(nextStatuses);
          setProductErrors(nextErrors);

          const allTerminal = (status.products || []).every(
            (entry) =>
              entry.status === 'SUCCESS' ||
              entry.status === 'FAILED' ||
              entry.status === 'RETRY_SCHEDULED',
          );

          const receivedProducts = new Set(
            (status.products || []).map((e) => e.product),
          );
          const allExpectedPresent = expectedProducts.every((p) =>
            receivedProducts.has(p),
          );

          if (
            allTerminal &&
            allExpectedPresent &&
            status.products?.length > 0
          ) {
            const anyFailed = (status.products || []).some(
              (entry) =>
                entry.status === 'FAILED' || entry.status === 'RETRY_SCHEDULED',
            );
            setHasFailure(anyFailed);
            setIsComplete(true);
            return;
          }
        } catch {
          // Status endpoint may 404 if propagation hasn't started yet — keep polling
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }

      if (!cancelledRef.current) {
        setHasTimedOut(true);
        setHasFailure(true);
        setIsComplete(true);
      }
    },
    [teamspaceId, tenantDataService],
  );

  useEffect(() => {
    cancelledRef.current = false;
    const fetchProductsAndPoll = async () => {
      try {
        const response =
          await tenantDataService.getAiPropagationProducts(teamspaceId);
        const productList = response?.products || [];
        setProducts(productList);

        const initialStatuses: Record<string, ProductStatus> = {};
        productList.forEach((p) => {
          initialStatuses[p] = 'waiting';
        });
        setProductStatuses(initialStatuses);

        if (productList.length === 0) {
          setIsComplete(true);
          return;
        }

        await pollStatus(productList);
      } catch {
        setIsComplete(true);
      }
    };

    fetchProductsAndPoll();

    return () => {
      cancelledRef.current = true;
    };
  }, [teamspaceId, tenantDataService, pollStatus]);

  const renderProductRow = (product: string, index: number) => {
    const status = productStatuses[product] || 'waiting';
    const label = PRODUCT_LABELS[product] || product;
    const isSuccess = status === 'success';
    const isFailed = status === 'failed';
    const isRunning = status === 'running';
    const error = productErrors[product];

    const statusText = isSuccess
      ? `${label} ${t('completed') || 'completed'}`
      : isFailed
        ? `${label} ${t('failed') || 'failed'}`
        : isRunning
          ? `${t('propagating') || 'Propagating'}...`
          : t('propagation-status-waiting') || 'Waiting...';

    return (
      <div
        key={product}
        data-testid={`propagation-product-row-${index}`}
        className="mb-4 grid grid-cols-12 items-center gap-4"
      >
        <div className="col-span-3">
          <span className="text-sm font-medium text-carbon-dark dark:text-gray-300">
            {label}
          </span>
        </div>
        <div className="col-span-9">
          <div className="mb-1 flex items-center gap-2">
            {isSuccess && (
              <CheckmarkFilled
                size={16}
                className="text-status-successGreen"
                aria-hidden="true"
              />
            )}
            {isFailed && (
              <ErrorFilled
                size={16}
                className="text-status-failRed"
                aria-hidden="true"
              />
            )}
            {isRunning && (
              <span
                className="inline-block size-4 animate-spin rounded-full border-2 border-brand-interactive border-t-transparent dark:border-brand-blue dark:border-t-transparent"
                role="status"
                aria-label={t('loading') || 'Loading'}
              />
            )}
            <span
              data-testid={`propagation-product-status-${index}`}
              className={`text-xs ${
                isFailed
                  ? 'font-medium text-status-failRed'
                  : 'text-carbon-textSecondary dark:text-brand-disabled'
              }`}
            >
              {statusText}
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-carbon-border dark:bg-carbon-textSecondary">
            <div
              data-testid={`propagation-product-fill-${index}`}
              className={`h-2 rounded-full transition-all duration-1000 ${
                isSuccess
                  ? 'w-full bg-status-successGreen'
                  : isFailed
                    ? 'w-full bg-status-failRed'
                    : isRunning
                      ? 'w-2/3 bg-brand-interactive'
                      : 'w-0'
              }`}
            />
          </div>
          {isFailed && error && (
            <p className="mt-1 text-xs text-status-failRed">{error}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="propagation-progress-container"
      className="flex flex-col gap-4"
    >
      <h3 className="text-sm font-semibold text-carbon-dark dark:text-white">
        {t('propagation-progress') || 'Integration Propagation Progress'}
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('propagation-progress-hint') ||
          'Your integration is being synced to the following products.'}
      </p>

      <div className="space-y-2">
        {products.map((product, idx) => renderProductRow(product, idx))}
      </div>

      {isComplete && hasTimedOut && (
        <p className="text-xs font-medium text-status-failRed">
          {t('propagation-timed-out') ||
            'Propagation is taking longer than expected. You may retry or close and check back later.'}
        </p>
      )}

      {isComplete && (
        <div className="flex justify-end gap-3 pt-2">
          {hasFailure && (
            <Button
              variant="outline"
              disabled={isRetrying}
              onClick={async () => {
                const failedProducts = Object.entries(productStatuses)
                  .filter(([, s]) => s === 'failed')
                  .map(([p]) => p);
                if (failedProducts.length === 0) return;
                setIsRetrying(true);
                try {
                  await tenantDataService.retryAiPropagation(
                    teamspaceId,
                    failedProducts,
                  );
                  if (cancelledRef.current) return;
                  // Reset and re-poll
                  setIsComplete(false);
                  setHasFailure(false);
                  setHasTimedOut(false);
                  setProductErrors({});
                  const resetStatuses = { ...productStatuses };
                  failedProducts.forEach((p) => {
                    resetStatuses[p] = 'waiting';
                  });
                  setProductStatuses(resetStatuses);
                  await pollStatus(products);
                } catch {
                  // retry request itself failed — stay on current screen
                } finally {
                  setIsRetrying(false);
                }
              }}
            >
              {isRetrying
                ? `${t('retry') || 'Retry'}...`
                : t('retry') || 'Retry'}
            </Button>
          )}
          <Button onClick={onDone}>
            {hasFailure ? t('close') || 'Close' : t('done') || 'Done'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PropagationProgress;
